/**
 * ErrorState — Phase 13.3 (2026-04-29) primitive
 *
 * Reusable error indicator with explicit recovery action.
 * Distinguishable from EmptyState: amber/red accent, warning icon,
 * explicit problem-stated copy.
 *
 * Per external research (Phase 13 scope §2 13.3.3):
 *   - Always include recovery action (retry / contact support / undo)
 *   - role="alert" + aria-live="assertive" (time-critical)
 *   - Color contrast 4.5:1 on amber/red text (WCAG AA)
 *   - Include error reference id for support workflow (Phase 13.2)
 */
// Phase 18 audit-fix (2026-04-29): removed PropTypes import — codebase
// doesn't use PropTypes elsewhere. JSDoc above is the type contract.
export default function ErrorState({
  message,
  errorId,
  action = null,
  contactSupport = true,
  ariaLabel,
  style: extraStyle = {},
}) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-label={ariaLabel || message}
      style={{ ...containerStyle, ...extraStyle }}
    >
      <div style={iconStyle} aria-hidden="true">⚠</div>
      <p style={messageStyle}>{message}</p>
      {errorId && (
        <p style={refStyle}>
          Reference: <code style={refCodeStyle}>{errorId}</code>
        </p>
      )}
      <div style={actionsRowStyle}>
        {action}
        {contactSupport && errorId && (
          <a
            href={`mailto:support@juuniai.com?subject=${encodeURIComponent(`Error ref: ${errorId}`)}&body=${encodeURIComponent(`I hit an error in Juuni AI.\n\nReference: ${errorId}\n\nWhat I was trying to do:\n`)}`}
            style={contactLinkStyle}
          >
            Contact support
          </a>
        )}
      </div>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────

const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px 24px',
  textAlign: 'center',
  gap: 10,
  // Phase 13.3: amber accent, distinguishable from neutral EmptyState
  background: 'rgba(212,146,42,0.06)',
  borderLeft: '4px solid #d4922a',
  borderRadius: 8,
};

const iconStyle = {
  fontSize: 28,
  color: '#d4922a',
};

const messageStyle = {
  margin: 0,
  fontSize: 14,
  lineHeight: 1.5,
  color: 'var(--text-primary, #14110c)',
  maxWidth: 540,
};

const refStyle = {
  margin: 0,
  fontSize: 12,
  color: 'var(--text-muted, #6b6254)',
  fontFamily: 'var(--font-mono, monospace)',
};

const refCodeStyle = {
  background: 'rgba(0,0,0,0.04)',
  padding: '2px 6px',
  borderRadius: 4,
};

const actionsRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  marginTop: 8,
};

const contactLinkStyle = {
  fontSize: 12,
  color: 'var(--text-muted, #6b6254)',
  textDecoration: 'underline',
  fontFamily: 'var(--font-mono, monospace)',
};
